import { GoFeatureFlagWebProvider } from '@openfeature/go-feature-flag-web-provider';
import {
  OpenFeature,
  ProviderEvents,
  type EvaluationContext,
} from '@openfeature/web-sdk';
import { sessionFlagContext } from '~/composables/useFeatureFlag';

export default defineNuxtPlugin({
  name: 'open-feature',
  async setup() {
    const { public: publicConfig } = useRuntimeConfig();
    const endpoint = publicConfig.goFeatureFlag?.endpoint as string | undefined;

    if (!endpoint) {
      return;
    }

    // Provider init talks to the relay. If anything goes wrong (network
    // failure, malformed response, timeout) we swallow it and bail out of
    // the plugin without providing $openFeatureClient — useFeatureFlag then
    // sees an undefined client and short-circuits hasAccessToFeature to
    // false. See ADR 20260521-feature-flags-openfeature-goff (fail-closed).
    const provider = new GoFeatureFlagWebProvider({ endpoint });
    try {
      await OpenFeature.setProviderAndWait(provider);
    } catch (err) {
      console.error('OpenFeature provider initialisation failed:', err);
      return;
    }

    const authStore = useAuthStore();
    const oidcStore = useOidcStore();
    const anonIdCookie = useCookie<string | null>('gp_anon_id');

    // useUserCountry resolves via SSR header → /api/requester → locale fallback,
    // and is only needed as a fallback when no Keycloak country is available.
    // Skip the fetch entirely when the user is already authenticated; the
    // Keycloak address claim takes priority either way.
    let inferredCountry: ComputedRef<string> | undefined;
    if (!authStore.isAuthenticated) {
      try {
        inferredCountry = await useUserCountry();
      } catch {
        inferredCountry = undefined;
      }
    }

    // Build the evaluation context from current store state. Reads stores at
    // call time (not at watcher-setup time) so a debounced run sees the
    // latest values, not a stale snapshot.
    const buildContext = (): EvaluationContext =>
      sessionFlagContext({
        authStore,
        oidcStore,
        publicConfig,
        anonId: anonIdCookie.value ?? undefined,
        ipCountry: inferredCountry?.value,
      });

    // authStore.user, oidcStore.parsedToken, and authStore.activeRegion are
    // updated across separate async boundaries during login, so a raw
    // watchEffect would fire (and round-trip to the relay) on each store
    // write. Coalesce adjacent updates onto the next microtask so login
    // produces one setContext call and we never evaluate against a
    // half-built context.
    //
    // `whenReady` resolves once the FIRST targeting context has been applied
    // (setProviderAndWait above only fetches against the empty bootstrap
    // context, so group/country targeting isn't in effect until the initial
    // setContext settles). Consumers that branch on a flag exactly once at
    // mount — e.g. the checkout flow picker — await this so an allow-listed
    // user isn't routed down the fallback path on a cold load. Resolves on
    // failure too: a rejected setContext leaves flags at their fail-closed
    // default, which is the same conservative behaviour as not waiting.
    const contextApplied = ref(false);
    let resolveReady: (() => void) | undefined;
    const whenReady = new Promise<void>((resolve) => {
      resolveReady = resolve;
    });

    let pending = false;
    watchEffect(() => {
      // touch every reactive dependency so the effect re-runs on any change
      void authStore.user;
      void oidcStore.parsedToken;
      void authStore.activeRegion;

      if (pending) {
        return;
      }
      pending = true;
      queueMicrotask(async () => {
        pending = false;
        try {
          await OpenFeature.setContext(buildContext());
        } catch (err) {
          // setContext can reject if the provider rejects the new context.
          // Swallow it: evaluations will keep returning their default (false)
          // until the next context update succeeds.
          console.error('OpenFeature setContext failed:', err);
        } finally {
          // Signal readiness after the first context attempt settles (only
          // the first call matters; later context changes just re-bump
          // flagVersion via the ContextChanged handler below).
          contextApplied.value = true;
          resolveReady?.();
          resolveReady = undefined;
        }
      });
    });

    const client = OpenFeature.getClient();

    // The OpenFeature provider stores flag values in plain JS state, so
    // useFeatureFlag() reads against a moving target Vue can't observe.
    // Bump a ref every time the provider's cache changes (initial fetchAll
    // finishing, websocket pushes, context-driven re-evaluations) so any
    // computed that touches it re-runs and the gated UI updates instead of
    // staying stuck on its first synchronous evaluation.
    const flagVersion = ref(0);
    const bump = () => {
      flagVersion.value++;
    };
    const handlerEvents = [
      ProviderEvents.Ready,
      ProviderEvents.ConfigurationChanged,
      ProviderEvents.ContextChanged,
      ProviderEvents.Stale,
      ProviderEvents.Error,
    ];
    for (const event of handlerEvents) {
      client.addHandler(event, bump);
    }

    // Under Vite HMR the plugin module can be re-evaluated without a full
    // page reload, which would otherwise accumulate handlers on the singleton
    // OpenFeature client. Remove them on dispose so each HMR cycle starts
    // from a clean slate. No-op in production.
    if (import.meta.hot) {
      import.meta.hot.dispose(() => {
        for (const event of handlerEvents) {
          client.removeHandler(event, bump);
        }
      });
    }

    return {
      provide: {
        openFeatureClient: client,
        openFeatureFlagVersion: flagVersion,
        openFeatureReady: whenReady,
        openFeatureContextApplied: contextApplied,
      },
    };
  },
});
